// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import axios from 'axios';
import {
  SESSION_ACTIVITY_EVENT,
  API_SESSION_ACTIVITY_DEBOUNCE_MS,
} from '../constants/sessionActivity';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8000';
const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 30000;
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1';

function parsePositiveInt(value, fallback) {
  if (value === undefined || value === null || String(value).trim() === '') {
    return fallback;
  }
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * POST /chat/: hybrid RAG + Ollama often exceeds default VITE_API_TIMEOUT (30s).
 * Default 300000 ms (5 min), same order as backend OLLAMA_TIMEOUT for typical setups.
 * Override: VITE_CHAT_TIMEOUT; falls back to VITE_VOICE_PROCESSING_TIMEOUT then 300000.
 */
export const CHAT_REQUEST_TIMEOUT_MS = parsePositiveInt(
  import.meta.env.VITE_CHAT_TIMEOUT,
  parsePositiveInt(import.meta.env.VITE_VOICE_PROCESSING_TIMEOUT, 300000)
);

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(API_TIMEOUT),
  // Don't set default Content-Type to allow FormData to set multipart/form-data
});

/**
 * Public API client: no Authorization header, no token refresh (one-time share links).
 */
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(API_TIMEOUT),
});

publicApiClient.interceptors.request.use(
  (config) => {
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

let lastApiSessionActivityAt = 0;

function dispatchSessionActivityFromAuthenticatedApi() {
  const token = localStorage.getItem('auth_token');
  if (!token) return;
  const now = Date.now();
  if (now - lastApiSessionActivityAt < API_SESSION_ACTIVITY_DEBOUNCE_MS) return;
  lastApiSessionActivityAt = now;
  window.dispatchEvent(new CustomEvent(SESSION_ACTIVITY_EVENT));
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Set Content-Type for JSON requests (not FormData)
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with auto-refresh
apiClient.interceptors.response.use(
  (response) => {
    dispatchSessionActivityFromAuthenticatedApi();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Call refresh endpoint
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token, refresh_token: newRefreshToken } = response.data;
        
        // Update tokens
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);
        
        // Update auth header
        apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
        
        // Process queued requests
        processQueue(null, access_token);
        isRefreshing = false;
        
        console.log('✅ Token refreshed successfully');
        
        // Retry original request
        return apiClient(originalRequest);
        
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        isRefreshing = false;
        
        console.log('❌ Token refresh failed, logging out');
        
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API Endpoints - Dynamic based on environment variables
export const API_ENDPOINTS = {
  // Health Check
  HEALTH: '/health',
  HEALTH_DETAILED: '/health/detailed',
  
  // Authentication & Authorization
  AUTH_LOGIN: `/api/${API_VERSION}/auth/login`,
  AUTH_REFRESH: `/api/${API_VERSION}/auth/refresh`,
  AUTH_LOGOUT: `/api/${API_VERSION}/auth/logout`,
  AUTH_ME: `/api/${API_VERSION}/auth/me`,
  
  // User Management
  USERS: `/api/${API_VERSION}/users`,
  USER_PROFILE: `/api/${API_VERSION}/users/profile`,
  
  // Team Management
  TEAMS: `/api/${API_VERSION}/teams/`,
  TEAMS_FOR_FILTERING: `/api/${API_VERSION}/teams/for-filtering`,
  
  // Voice Processing & AI Services
  VOICE_UPLOAD: `/api/${API_VERSION}/voice/upload`,
  VOICE_TASKS: `/api/${API_VERSION}/voice/tasks`,
  VOICE_TASK_DETAIL: `/api/${API_VERSION}/voice/tasks`,
  VOICE_STATUS: `/api/${API_VERSION}/voice/status`,
  /** GET — auth; canonical list for upload + Translation Glossary (avoids stale dist JSON) */
  VOICE_UPLOAD_LANGUAGES: `/api/${API_VERSION}/voice/upload-languages`,
  VOICE_CANCEL: `/api/${API_VERSION}/voice/tasks`,
  VOICE_DELETE: `/api/${API_VERSION}/voice/tasks`,
  VOICE_RETRY: `/api/${API_VERSION}/voice/tasks`,           // /api/voice/tasks/{id}/retry
  VOICE_TASK_RERUN: `/api/${API_VERSION}/voice/tasks`,      // /api/voice/tasks/{id}/rerun
  VOICE_PROCESSED_FILES: `/api/${API_VERSION}/voice/tasks`,
  VOICE_REMOVE: `/api/${API_VERSION}/voice/tasks`,
  VOICE_TRANSCRIPTION: `/api/${API_VERSION}/voice/tasks`,  // /api/voice/tasks/{id}/transcription
  VOICE_METADATA: `/api/${API_VERSION}/voice/tasks`,        // /api/voice/tasks/{id}/metadata
  VOICE_DIAGNOSTIC_LOGS: `/api/${API_VERSION}/voice/tasks`, // /api/voice/tasks/{id}/diagnostic-logs
  // PATCH /{taskId}/analysis/override-score — matrix: { metric_key, overridden_score, reason }
  // boolean QA: { metric_key, overridden_result: "Yes"|"No", reason }
  VOICE_ANALYSIS_OVERRIDE: `/api/${API_VERSION}/voice/tasks`,
  /** POST /voice/tasks/{taskId}/share-link — auth required; returns one-time token */
  VOICE_TASK_SHARE_LINK: `/api/${API_VERSION}/voice/tasks`,
  /** GET /public/voice/share/{token} — no auth; consumes token on first success */
  VOICE_PUBLIC_SHARE: `/api/${API_VERSION}/public/voice/share`,

  // QA Criteria Management
  QA_CRITERIA_LIST: `/api/${API_VERSION}/qa-criteria`,           // GET - List all criteria
  QA_CRITERIA_VOICE_PROCESSING: `/api/${API_VERSION}/qa-criteria/voice-processing`, // GET - List criteria for voice processing (excludes templates)
  QA_CRITERIA_DETAIL: `/api/${API_VERSION}/qa-criteria`,         // GET /{id} - Get full details
  QA_CRITERIA_SUMMARY: `/api/${API_VERSION}/qa-criteria`,        // GET /{id}/summary - Get summary
  QA_CRITERIA_CREATE: `/api/${API_VERSION}/qa-criteria`,         // POST - Create new
  QA_CRITERIA_UPDATE: `/api/${API_VERSION}/qa-criteria`,         // PUT /{id} - Update
  QA_CRITERIA_DELETE: `/api/${API_VERSION}/qa-criteria`,         // DELETE /{id} - Delete
  QA_CRITERIA_DUPLICATE: `/api/${API_VERSION}/qa-criteria`,      // POST /{id}/duplicate
  QA_CRITERIA_ACTIVATE: `/api/${API_VERSION}/qa-criteria`,       // PATCH /{id}/activate
  QA_CRITERIA_SET_DEFAULT: `/api/${API_VERSION}/qa-criteria`,    // PATCH /{id}/set-default
  
  // Chat & AI Interaction
  CHAT: `/api/${API_VERSION}/chat/`,
  CHAT_RAG_CONTEXT: `/api/${API_VERSION}/chat/rag-context`,
  CHAT_GREETING: `/api/${API_VERSION}/chat/greeting`,
  CHAT_SESSIONS: `/api/${API_VERSION}/chat/sessions`,
  // Session messages: GET/POST `${CHAT_SESSIONS}/{sessionId}/messages` (no legacy /chat/history route)

  // File Management
  FILES_UPLOAD: `/api/${API_VERSION}/files/upload`,
  FILES_LIST: `/api/${API_VERSION}/files`,
  FILES_DOWNLOAD: `/api/${API_VERSION}/files/download`,
  FILES_DELETE: `/api/${API_VERSION}/files/delete`,
  
  // System Settings
  SETTINGS: `/api/${API_VERSION}/settings`,
  SETTINGS_CATEGORIES: `/api/${API_VERSION}/settings/categories`,
  SETTINGS_CATEGORY_QA_CONTEXT: `/api/${API_VERSION}/settings/category/qa_context`,
  
  // Word Correction
  WORD_CORRECTION: `/api/${API_VERSION}/settings/word-correction`,
  WORD_CORRECTION_GENERATE: `/api/${API_VERSION}/settings/word-correction/generate`,
  
  // QA Context
  QA_CONTEXT_GENERATE_PREVIEW: `/api/${API_VERSION}/qa-context/generate-preview`,
  
  // QA Dashboard
  QA_DASHBOARD_SUMMARY: `/api/${API_VERSION}/qa-dashboard/summary`,
  QA_DASHBOARD_RECORDS: `/api/${API_VERSION}/qa-dashboard/records`,
  QA_DASHBOARD_FILTER_OPTIONS: `/api/${API_VERSION}/qa-dashboard/filter-options`,

  // QA Prompts (versioned voice/QA prompts - review and rollback)
  QA_PROMPTS: `/api/${API_VERSION}/qa-prompts`,

  /** Model Management — `${MODEL_FINETUNE}/models/active`, voice-llm-providers, set-active, delete, costs */
  MODEL_FINETUNE: `/api/${API_VERSION}/model-finetune`,
  /** OpenAI organization costs (UTC month) — requires OPENAI_ADMIN_API_KEY on server */
  MODEL_FINETUNE_OPENAI_COSTS_MONTH: `/api/${API_VERSION}/model-finetune/openai/organization-costs-month`,

  /** Knowledge Management (source-of-truth + search; paths under `/knowledge/...`) */
  KNOWLEDGE: `/api/${API_VERSION}/knowledge`,

  /** Chat Prompts (system prompts / AI persona) */
  SYSTEM_PROMPTS: `/api/${API_VERSION}/system-prompts`,
};

export default apiClient;
